/*
 * Shop by Vibe parallax.
 * Shifts each card's artwork vertically inside its frame as the card moves
 * through the viewport. Lightweight: a single rAF loop driven by scroll/resize,
 * only animates cards currently in view, and respects reduced-motion.
 */
class FluevogShopByVibe extends HTMLElement {
  connectedCallback() {
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.enabled = this.dataset.parallax === 'true' && !this.reducedMotion;
    this.targets = Array.from(this.querySelectorAll('[data-parallax-target]'));

    if (!this.enabled || this.targets.length === 0) return;

    // Max travel as a fraction of the media height (CSS gives 18% of slack each side).
    // Soften slightly on coarse/narrow viewports so scroll stays smoother.
    let strength = parseFloat(this.dataset.parallaxStrength || '70') / 100;
    if (window.matchMedia('(pointer: coarse), (max-width: 749px)').matches) {
      strength *= 0.65;
    }
    this.maxShift = 0.18 * strength;

    this.visible = new Set();
    this.ticking = false;

    this.onScroll = this.onScroll.bind(this);
    this.update = this.update.bind(this);

    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.visible.add(entry.target);
          } else {
            this.visible.delete(entry.target);
          }
        }
        this.onScroll();
      },
      { rootMargin: '10% 0px' }
    );

    this.targets.forEach((t) => this.observer.observe(t));

    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });
    this.onScroll();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
    this.observer?.disconnect();
  }

  onScroll() {
    if (this.ticking) return;
    this.ticking = true;
    requestAnimationFrame(this.update);
  }

  update() {
    this.ticking = false;
    const vh = window.innerHeight || document.documentElement.clientHeight;

    this.visible.forEach((target) => {
      const media = target.parentElement;
      if (!media) return;
      const rect = media.getBoundingClientRect();

      // progress: -1 when the card is entering at the bottom, +1 when leaving at the top.
      const center = rect.top + rect.height / 2;
      const progress = (center - vh / 2) / ((vh + rect.height) / 2);
      const clamped = Math.max(-1, Math.min(1, progress));
      const shift = -clamped * this.maxShift * rect.height;

      target.style.transform = `translate3d(0, ${shift.toFixed(2)}px, 0)`;
    });
  }
}

if (!customElements.get('fv-shop-by-vibe')) {
  customElements.define('fv-shop-by-vibe', FluevogShopByVibe);
}
